import React from 'react';
import { FileText, Cookie, ShieldAlert, RefreshCcw, PhoneCall } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { SUPPORT_EMAIL, SUPPORT_EMAIL_MAILTO } from '@/config/links';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Terms & Conditions"
        description="Terms and conditions governing the use of Boxd Logistics services and platform."
        canonical="https://boxdlogistics.in/terms.php"
        robots="noindex, follow"
      />
      <Navbar />

      <section className="relative pt-32 pb-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        ></div>
        <div className="absolute -top-12 right-0 w-72 h-72 bg-gradient-to-br from-blue-300/20 to-blue-700/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-900 px-4 py-2 text-sm font-semibold mb-6">
            <FileText className="w-4 h-4" />
            Terms of Service
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl">
            Welcome to Boxd Logistics. This website is owned and operated by Nexar Logistic Private Limited. By using this website, you agree to the terms and conditions outlined below.
          </p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto space-y-10">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Use of Website</h2>
            <p className="text-gray-600 leading-relaxed">
              By accessing this website, you accept these terms and conditions in full. Do not continue to use Boxd Logistics&apos; website if you do not accept all of the terms and conditions stated on this page.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              The terms &quot;Client&quot;, &quot;You&quot;, and &quot;Your&quot; refer to you, the person accessing this website and accepting the company&apos;s terms. &quot;The Company&quot;, &quot;We&quot;, &quot;Our&quot;, and &quot;Us&quot; refer to Boxd Logistics.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Cookies</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              We employ the use of cookies. By accessing Boxd Logistics&apos; website, you agree to use cookies in agreement with our Privacy Policy.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              Cookies are used to enable functionality and improve user experience. Some affiliate or advertising partners may also use cookies.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">License</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Unless otherwise stated, Boxd Logistics and/or its licensors own the intellectual property rights for all material on this website. All intellectual property rights are reserved.
            </p>
            <p className="text-gray-700 font-semibold mb-3">You must not:</p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>Republish material from Boxd Logistics&apos; website</span>
              </li>
              <li className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>Sell, rent, or sub-license material from Boxd Logistics&apos; website</span>
              </li>
              <li className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>Reproduce, duplicate, or copy material from Boxd Logistics&apos; website</span>
              </li>
              <li className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-blue-800 mt-0.5" />
                <span>Redistribute content from Boxd Logistics&apos; website</span>
              </li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-6">This agreement shall begin on the date hereof.</p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Account Terms</h2>
            <ul className="space-y-3 text-gray-700 leading-relaxed">
              <li>You must be at least 18 years old to use this Service.</li>
              <li>You must provide accurate and complete registration details, including legal name, address, and a valid email address.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.</li>
              <li>You must not use the platform for illegal, fraudulent, or unauthorized purposes, or in violation of applicable laws of India or your jurisdiction.</li>
              <li>You are solely responsible for all data and content uploaded through your account.</li>
              <li>You must not upload or transmit malware, viruses, worms, or any destructive code.</li>
              <li>Any breach of these Account Terms may result in suspension or immediate termination of your account.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">General Conditions</h2>
            <ul className="space-y-3 text-gray-700 leading-relaxed">
              <li>By using our platform, you agree to these Terms and to our Privacy Policy.</li>
              <li>Boxd Logistics may refuse service to any user for valid business, compliance, legal, or security reasons.</li>
              <li>The Service is provided on an &quot;as is&quot; and &quot;as available&quot; basis, without express or implied warranties.</li>
              <li>We do not guarantee uninterrupted, timely, secure, or error-free operation, nor guaranteed business outcomes from use of the Service.</li>
              <li>Your content (excluding payment card data) may be transmitted over networks and adapted for technical compatibility.</li>
              <li>We may remove content or suspend accounts containing unlawful, abusive, or rights-violating material.</li>
              <li>You agree not to reproduce, resell, duplicate, or exploit any part of the Service without written consent from Boxd Logistics.</li>
              <li>Abusive, threatening, or defamatory conduct toward our team, partners, or users may lead to immediate account action.</li>
              <li>You agree to indemnify and hold Boxd Logistics, its affiliates, officers, employees, and partners harmless against third-party claims arising from your breach of these Terms or applicable law.</li>
              <li>Failure by Boxd Logistics to enforce any provision shall not be treated as waiver of such provision.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Payment of Fees</h2>
            <ul className="space-y-3 text-gray-700 leading-relaxed">
              <li>Applicable plans and billing cycles may be monthly, quarterly, half-yearly, or yearly, as agreed with Boxd Logistics.</li>
              <li>Invoices must be paid within 7 days from invoice date unless otherwise agreed in writing.</li>
              <li>All listed fees are exclusive of applicable taxes, levies, duties, and statutory charges.</li>
              <li>Subscription or platform fees are non-refundable unless expressly stated otherwise in a written commercial agreement.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Cancellation and Termination</h2>
            <ul className="space-y-3 text-gray-700 leading-relaxed">
              <li>Upon cancellation of your account, your access may be revoked and related data may be removed as per our retention policies.</li>
              <li>Boxd Logistics may suspend or terminate services where fraud, misuse, or material breach is suspected.</li>
              <li>Where required by law, legal process, or law-enforcement directions, accounts may be restricted immediately.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Restricted and Prohibited Items</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              You shall not offer, trade, ship, or attempt to ship items prohibited or restricted by applicable law or carrier policy. This includes, without limitation:
            </p>
            <ul className="space-y-3 text-gray-700 leading-relaxed">
              <li>Illegal financial instruments, contraband, and prohibited securities transactions.</li>
              <li>Wildlife products, protected animal parts, or regulated biological materials.</li>
              <li>Weapons, explosives, ammunition, and hazardous or combustible goods not legally permitted for shipment.</li>
              <li>Liquor, tobacco, narcotics, psychotropic substances, and other controlled intoxicants unless legally authorized.</li>
              <li>Restricted antiquities, art treasures, counterfeit goods, or any infringing/prohibited products.</li>
              <li>Any product or service that violates consumer protection, courier, customs, or trade compliance requirements.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Limitations</h2>
            <p className="text-gray-600 leading-relaxed">
              In no event shall Boxd Logistics or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use Boxd Logistics&apos; website.
            </p>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <RefreshCcw className="w-5 h-5 text-blue-900 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Changes to Terms</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Boxd Logistics reserves the right to revise these terms at any time without notice. By using this website, you are agreeing to be bound by the current version of these Terms and Conditions.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <PhoneCall className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Contact Us</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions or concerns about these Terms of Service, please contact us.
            </p>
            <div className="mt-4">
              <a href={SUPPORT_EMAIL_MAILTO} className="text-blue-800 hover:text-blue-900 font-semibold">
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsConditions;
